/**
 * Formatting shared by the donor app and the admin portal.
 *
 * Money and dates appear on nearly every screen; formatting them in one place
 * is what stops "Rs. 16,000" on one screen and "PKR 16000" on another.
 */

/** Minor units per major unit. PKR has no circulating subunit in practice. */
const MINOR_UNITS: Record<string, number> = { PKR: 100, USD: 100 };

export interface Money {
  /** Integer amount in minor units (paisa for PKR). Never a float. */
  amountMinor: number;
  /** ISO 4217 code. */
  currency: string;
}

const minorPerMajor = (currency: string): number => MINOR_UNITS[currency] ?? 100;

/**
 * The words a formatted value needs, for whichever language is on screen.
 *
 * Set once when the donor switches language rather than threaded through
 * forty call sites: there is only ever one language on screen, and a price
 * that stayed in English under Urdu text was the giveaway that formatting had
 * been left out of the translation.
 */
export interface FormatLocale {
  /** Currency symbol by ISO code, e.g. `{ PKR: 'روپے' }`. */
  currency?: Record<string, string>;
  /** Twelve short month names, January first. */
  months?: readonly string[];
  person?: string;
  people?: string;
  day?: string;
  days?: string;
  meal?: string;
  meals?: string;
}

let locale: FormatLocale = {};

export function setFormatLocale(next: FormatLocale): void {
  locale = next;
}

export const toMajor = (m: Money): number => m.amountMinor / minorPerMajor(m.currency);

export const fromMajor = (amount: number, currency = 'PKR'): Money => ({
  amountMinor: Math.round(amount * minorPerMajor(currency)),
  currency,
});

/**
 * Format money the way the artwork does: "Rs. 16,000".
 *
 * Whole amounts drop the decimal part, which is how every mockup renders PKR.
 * Fractional amounts keep two places so a refund of Rs. 16,000.50 is not
 * silently rounded on screen.
 */
export function formatMoney(m: Money, options: { withSymbol?: boolean } = {}): string {
  const { withSymbol = true } = options;
  const major = toMajor(m);
  const isWhole = Number.isInteger(major);
  const body = new Intl.NumberFormat('en-PK', {
    minimumFractionDigits: isWhole ? 0 : 2,
    maximumFractionDigits: isWhole ? 0 : 2,
  }).format(major);
  if (!withSymbol) return body;
  const symbol = locale.currency?.[m.currency] ?? (m.currency === 'PKR' ? 'Rs.' : m.currency);
  return `${symbol} ${body}`;
}

/** "Rs. 160 per person" — used on meal cards and summaries. */
export const formatUnitPrice = (m: Money): string => `${formatMoney(m)} per person`;

/**
 * Compact display of large counts, matching the impact footer: 25,000 → "25,000+".
 *
 * The "+" is a display convention only; the API returns exact values (gap G-12).
 * Rounding down to a readable step keeps the footer honest — it never claims
 * more than has happened.
 */
export function formatImpactCount(value: number): string {
  if (value < 1000) return String(value);
  const step = value >= 10000 ? 1000 : value >= 1000 ? 100 : 10;
  const rounded = Math.floor(value / step) * step;
  return `${new Intl.NumberFormat('en-US').format(rounded)}+`;
}

export const formatCount = (value: number): string =>
  new Intl.NumberFormat('en-US').format(value);

/** "100 People" / "1 Person". */
export const formatPeople = (count: number): string =>
  `${formatCount(count)} ${
    count === 1 ? locale.person ?? 'Person' : locale.people ?? 'People'
  }`;

/** "7 days" / "1 day". */
export const formatDays = (count: number): string =>
  `${formatCount(count)} ${count === 1 ? locale.day ?? 'day' : locale.days ?? 'days'}`;

/** "4 meals" / "1 meal": the deliveries a tiffin plan makes. */
export const formatMeals = (count: number): string =>
  `${formatCount(count)} ${count === 1 ? locale.meal ?? 'meal' : locale.meals ?? 'meals'}`;

/** `YYYY-MM-DD` with no time component. */
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parse a value into a Date without shifting calendar dates.
 *
 * `new Date('2026-08-30')` is interpreted as UTC midnight, which renders as
 * the 29th anywhere behind UTC — so a distribution booked for the 30th would
 * be shown to the donor as the 29th. A date-only string has no timezone, so it
 * is parsed in local time; timestamps keep their offset as sent.
 */
const toDate = (value: Date | string): Date => {
  if (value instanceof Date) return value;
  if (DATE_ONLY_RE.test(value)) {
    const [year, month, day] = value.split('-').map(Number) as [number, number, number];
    return new Date(year, month - 1, day);
  }
  return new Date(value);
};

/** "30 Aug, 2026" — the date format used throughout the artwork. */
export function formatDate(value: Date | string): string {
  const d = toDate(value);
  if (Number.isNaN(d.getTime())) return '—';
  const day = d.getDate();
  const month = locale.months?.[d.getMonth()] ?? d.toLocaleString('en-GB', { month: 'short' });
  return `${day} ${month}, ${d.getFullYear()}`;
}

/** "10:32 AM". */
export function formatTime(value: Date | string): string {
  const d = toDate(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d
    .toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
    .replace(/^0/, '');
}

/** "20 Aug, 2026 • 10:32 AM" — the timeline and notification stamp. */
export const formatDateTime = (value: Date | string): string =>
  `${formatDate(value)} • ${formatTime(value)}`;

/** "Aug 2026" — the Impact screen's "Since" row. */
export function formatMonthYear(value: Date | string): string {
  const d = toDate(value);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.toLocaleString('en-GB', { month: 'short' })} ${d.getFullYear()}`;
}

/** ISO `YYYY-MM-DD` in local time, for date inputs and API payloads. */
export function toISODate(value: Date | string): string {
  const d = toDate(value);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Mask a saved card for display: "Visa •••• 4242".
 *
 * Takes the brand and last four only. A full PAN must never reach the client,
 * so this function deliberately cannot accept one.
 */
export const formatCardLabel = (brand: string, last4: string): string =>
  `${brand} •••• ${last4}`;

/** "08/28" from a stored expiry. */
export const formatCardExpiry = (month: number, year: number): string =>
  `${String(month).padStart(2, '0')}/${String(year).slice(-2)}`;

/** Mask a phone number for anonymous or partial display: "+92 300 •••4567". */
export function maskPhone(phone: string): string {
  const trimmed = phone.replace(/\s+/g, '');
  if (trimmed.length < 4) return '•••';
  return `${trimmed.slice(0, -4).replace(/\d(?=\d{0,})/g, (c, i) => (i < 6 ? c : '•'))}${trimmed.slice(-4)}`;
}

/** The label shown wherever an anonymous donor would otherwise be named. */
export const ANONYMOUS_DONOR_LABEL = 'Anonymous';

export const displayDonorName = (name: string | null, anonymous: boolean): string =>
  anonymous || !name ? ANONYMOUS_DONOR_LABEL : name;
